import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AppError, SyncSaveState } from '@/types'
import { repositories } from '@/services/repositories'
import { isAppError } from '@/lib/supabase/errors'
import { isCloudConfigured } from '@/lib/supabase/client'
import { useAuth } from '@/features/auth'
import { useSync } from '@/services/sync'
import { todayLocalDate } from '@/domain/recurrence'
import {
  Banner,
  Card,
  ErrorState,
  SaveBar,
  Skeleton,
  SyncStatus,
  TextArea,
  useToast,
} from '@/components/ui'
import { PageHeader } from '@/components/navigation'
import { DropletIcon, MoonIcon, NoteIcon } from '@/components/icons'
import { HydrationTracker } from './components/HydrationTracker'
import { SleepSection } from './components/SleepSection'
import { HealthLibrary } from './components/HealthLibrary'
import { ScheduleBuilder } from './components/ScheduleBuilder'
import { RecentHistory } from './components/RecentHistory'
import { errorMessage } from './saveState'
import './health.css'

type DailyDraft = {
  hydration: number
  sleepHours: number
  sleepQuality: number
  notes: string
}

const EMPTY_DRAFT: DailyDraft = { hydration: 0, sleepHours: 0, sleepQuality: 3, notes: '' }

const NOT_CONFIGURED_ERROR: AppError = {
  code: 'SUPABASE_NOT_CONFIGURED',
  userMessage:
    'De cloudverbinding is niet ingesteld. Je gegevens blijven op dit apparaat bewaard.',
  retryable: false,
  source: 'database',
}

function sameDraft(a: DailyDraft, b: DailyDraft): boolean {
  return (
    a.hydration === b.hydration &&
    a.sleepHours === b.sleepHours &&
    a.sleepQuality === b.sleepQuality &&
    a.notes === b.notes
  )
}

/**
 * Gezondheid — daily hydration/sleep/notes, supplement & medication library,
 * structured schedule builder with Planner sync, and recent history.
 */
export default function HealthPage() {
  const { status, userId } = useAuth()
  const { online, pendingWrites } = useSync()
  const { showToast } = useToast()

  const today = useMemo(() => todayLocalDate(), [])

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<AppError | null>(null)
  const [baseline, setBaseline] = useState<DailyDraft>(EMPTY_DRAFT)
  const [draft, setDraft] = useState<DailyDraft>(EMPTY_DRAFT)
  const [saveState, setSaveState] = useState<SyncSaveState | null>(null)
  const [saving, setSaving] = useState(false)
  const [announcement, setAnnouncement] = useState('')

  const dirty = !sameDraft(draft, baseline)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const daily = await repositories.health.getDaily(today)
      const loaded: DailyDraft = daily
        ? {
            hydration: daily.hydration,
            sleepHours: daily.sleepHours,
            sleepQuality: daily.sleepQuality,
            notes: daily.notes,
          }
        : EMPTY_DRAFT
      setBaseline(loaded)
      setDraft(loaded)
    } catch (error) {
      setLoadError(
        isAppError(error)
          ? error
          : {
              code: 'UNKNOWN',
              userMessage: errorMessage(error),
              retryable: true,
              source: 'database',
            },
      )
    } finally {
      setLoading(false)
    }
  }, [today])

  useEffect(() => {
    if (status === 'authenticated') {
      void load()
    }
  }, [status, load])

  const handleSaveDaily = async () => {
    setSaving(true)
    setSaveState('syncing')
    try {
      const saved = await repositories.health.saveDaily({ date: today, ...draft })
      const savedDraft: DailyDraft = {
        hydration: saved.hydration,
        sleepHours: saved.sleepHours,
        sleepQuality: saved.sleepQuality,
        notes: saved.notes,
      }
      setBaseline(savedDraft)
      setDraft(savedDraft)
      // When the device is offline the write is pending synchronization.
      setSaveState(online ? 'cloud_saved' : 'sync_queued')
      showToast({ message: 'Gezondheid van vandaag opgeslagen', tone: 'success' })
      setAnnouncement('Je gezondheidsgegevens van vandaag zijn opgeslagen.')
    } catch (error) {
      // Offline: the change stays on the device and waits for synchronization.
      setSaveState(online ? 'sync_failed' : 'sync_queued')
      const message = errorMessage(error)
      showToast({ message, tone: 'error' })
      setAnnouncement(`Opslaan mislukt. ${message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleCancelDaily = () => {
    setDraft(baseline)
    setAnnouncement('Je wijzigingen zijn teruggezet.')
  }

  if (status === 'loading' || (status === 'authenticated' && loading)) {
    return (
      <>
        <PageHeader title="Gezondheid" />
        <Skeleton lines={6} />
      </>
    )
  }

  if (!isCloudConfigured()) {
    return (
      <>
        <PageHeader title="Gezondheid" />
        <ErrorState title="Geen cloudverbinding" error={NOT_CONFIGURED_ERROR} />
      </>
    )
  }

  if (loadError) {
    if (loadError.code === 'SUPABASE_NOT_CONFIGURED') {
      return (
        <>
          <PageHeader title="Gezondheid" />
          <ErrorState title="Geen cloudverbinding" error={loadError} />
        </>
      )
    }
    return (
      <>
        <PageHeader title="Gezondheid" />
        <ErrorState error={loadError} onRetry={() => void load()} />
      </>
    )
  }

  if (!userId) {
    return (
      <>
        <PageHeader title="Gezondheid" />
        <Skeleton lines={6} />
      </>
    )
  }

  return (
    <div className="health-page">
      <PageHeader title="Gezondheid" subtitle="Houd je dagelijkse gezondheid rustig bij." />

      <p className="visually-hidden" role="status" aria-live="polite">
        {announcement}
      </p>

      {pendingWrites > 0 ? (
        <Banner tone="info">
          {pendingWrites} {pendingWrites === 1 ? 'wijziging wacht' : 'wijzigingen wachten'} op
          synchronisatie.
        </Banner>
      ) : null}

      {/* 1–4. Daily health: hydration, sleep, quality, notes (one daily record) */}
      <section className="health-section" aria-labelledby="health-daily-title">
        <h2 className="health-section-title" id="health-daily-title">
          <DropletIcon size={20} className="health-section-icon" />
          Vandaag
        </h2>
        <Card padding="md" className="health-section">
          <HydrationTracker
            value={draft.hydration}
            onChange={(hydration) => setDraft((d) => ({ ...d, hydration }))}
            disabled={saving}
          />
        </Card>
        <Card padding="md" className="health-section">
          <h3 className="health-section-title">
            <MoonIcon size={18} className="health-section-icon" />
            Slaap
          </h3>
          <SleepSection
            sleepHours={draft.sleepHours}
            onSleepHoursChange={(sleepHours) => setDraft((d) => ({ ...d, sleepHours }))}
            sleepQuality={draft.sleepQuality}
            onSleepQualityChange={(sleepQuality) => setDraft((d) => ({ ...d, sleepQuality }))}
            disabled={saving}
          />
        </Card>
        <Card padding="md" className="health-section">
          <h3 className="health-section-title">
            <NoteIcon size={18} className="health-section-icon" />
            Notities van vandaag
          </h3>
          <TextArea
            label="Notities"
            value={draft.notes}
            onChange={(notes) => setDraft((d) => ({ ...d, notes }))}
            rows={3}
            placeholder="Hoe voelde je vandaag?"
          />
        </Card>
        <SyncStatus state={saveState} />
      </section>

      {/* 5. Supplement & medication library */}
      <HealthLibrary today={today} onAnnounce={setAnnouncement} />

      {/* 6. Structured schedule builder with Planner sync */}
      <ScheduleBuilder userId={userId} onAnnounce={setAnnouncement} />

      {/* 7. Recent history */}
      <RecentHistory today={today} />

      {/* 8. Explicit save state for the daily record */}
      <SaveBar
        visible={dirty}
        saving={saving}
        saveLabel="Gezondheid opslaan"
        onSave={() => void handleSaveDaily()}
        onCancel={handleCancelDaily}
        state={saveState}
      />
    </div>
  )
}
